import {
  KAKAO_MAX_DESCRIPTION,
  KAKAO_MAX_TITLE,
  KAKAO_SHARED_CARD_TEMPLATE_ID,
} from "../kakao.config";
import { resolveDeckCardKakaoImageUrl } from "../resolveDeckCardKakaoImageUrl";
import type {
  DeckCardKakaoShareInput,
  DeckCardKakaoShareLabels,
  KakaoFeedButton,
  KakaoSendCustomPayload,
  KakaoSendDefaultFeedPayload,
  KakaoWebLink,
} from "../kakao.types";
// import {
//   FAN_CARD_KAKAO_DEFAULT_BUTTON_KO,
//   FAN_CARD_KAKAO_TEMPLATE_VARS,
// } from "../fanCard/fanCardKakaoTemplate";
import { PUBLIC_SITE_URL } from "@/config/env";
import { truncateKakaoText } from "@/shared/utils/kakao/truncateKakaoText";
import {
  buildDeckCardSharePath,
  buildDeckCardShareUrl,
} from "./buildDeckCardShareUrl";
import { FAN_CARD_KAKAO_DEFAULT_BUTTON_KO, FAN_CARD_KAKAO_TEMPLATE_VARS } from "@/shared/constants/cardKakaoTemplate";

export type DeckCardKakaoPayload = {
  shareUrl: string;
  imageUrl: string;
  templateArgs: Record<string, string>;
  feed: KakaoSendDefaultFeedPayload;
  custom: KakaoSendCustomPayload | null;
  usesCustomTemplate: boolean;
};

const buildWebLink = (shareUrl: string): KakaoWebLink => ({
  mobileWebUrl: shareUrl,
  webUrl: shareUrl,
});

export const buildDeckCardKakaoPayload = (
  input: DeckCardKakaoShareInput,
  labels: DeckCardKakaoShareLabels = {},
): DeckCardKakaoPayload => {
  const shareInput = {
    kind: input.kind,
    mainCardId: input.mainCardId,
    senderUserId: input.senderUserId,
    demoCardId: input.demoCardId,
  };
  const shareUrl = buildDeckCardShareUrl(shareInput);
  const cardPath = buildDeckCardSharePath(shareInput);

  /** Matches Kakao template Component Link: https://www.thehope.one/${card_path} */
  const templateSiteOrigin =
    PUBLIC_SITE_URL.replace(/\/+$/, "") || "https://www.thehope.one";
  const templateShareUrl = buildDeckCardShareUrl({
    ...shareInput,
    origin: templateSiteOrigin,
  });
  const templateCardPath = buildDeckCardSharePath({
    ...shareInput,
    origin: templateSiteOrigin,
  });

  const senderName =
    input.senderName.trim() || input.talentName.trim() || "Creator";

  const resolvedCardTitle =
    labels.cardTitle ??
    input.cardTitle?.trim() ??
    `${input.talentName.trim() || "Creator"} Card`;

  const cardTitle = truncateKakaoText(resolvedCardTitle, KAKAO_MAX_TITLE);

  const shortHint = truncateKakaoText(
    labels.shortHint ??
      input.shortHint ??
      "Explore this card on TaaSNet.",
    KAKAO_MAX_DESCRIPTION,
  );

  const senderLine = truncateKakaoText(
    labels.senderLine ?? `${senderName} shared a card`,
    KAKAO_MAX_DESCRIPTION,
  );

  const buttonTitle =
    labels.buttonTitle?.trim() || FAN_CARD_KAKAO_DEFAULT_BUTTON_KO;

  const imageUrl = resolveDeckCardKakaoImageUrl({
    coverImageUrl: input.coverImageUrl,
    imageUrl: input.imageUrl,
  });
  const link = buildWebLink(shareUrl);

  const button: KakaoFeedButton = {
    title: buttonTitle,
    link,
  };

  const templateArgs: Record<string, string> = {
    [FAN_CARD_KAKAO_TEMPLATE_VARS.senderName]: senderName,
    [FAN_CARD_KAKAO_TEMPLATE_VARS.senderLine]: senderLine,
    [FAN_CARD_KAKAO_TEMPLATE_VARS.cardTitle]: cardTitle,
    [FAN_CARD_KAKAO_TEMPLATE_VARS.shortHint]: shortHint,
    [FAN_CARD_KAKAO_TEMPLATE_VARS.cardUrl]: templateShareUrl,
    [FAN_CARD_KAKAO_TEMPLATE_VARS.cardPath]: templateCardPath,
    [FAN_CARD_KAKAO_TEMPLATE_VARS.imageUrl]: imageUrl,
    [FAN_CARD_KAKAO_TEMPLATE_VARS.buttonTitle]: buttonTitle,
  };

  const feed: KakaoSendDefaultFeedPayload = {
    objectType: "feed",
    content: {
      title: cardTitle,
      description: `${senderLine}\n${shortHint}`,
      imageUrl,
      link,
    },
    buttonTitle,
    buttons: [button],
  };

  const usesCustomTemplate =
    Number.isFinite(KAKAO_SHARED_CARD_TEMPLATE_ID) &&
    KAKAO_SHARED_CARD_TEMPLATE_ID > 0;

  const custom = usesCustomTemplate
    ? {
        templateId: KAKAO_SHARED_CARD_TEMPLATE_ID,
        templateArgs,
      }
    : null;

  return {
    shareUrl,
    imageUrl,
    templateArgs,
    feed,
    custom,
    usesCustomTemplate,
  };
};
